import VisuallyHidden from '@reach/visually-hidden';
import React from 'react';
import { DialogContent, DialogOverlay } from '@reach/dialog';
import styled, { css } from 'styled-components';
import { focusRing } from '@pleo-io/telescope-focus-ring';
import { Text } from '@pleo-io/telescope-text';
import { getColor } from '@pleo-io/telescope-theme';
import { shade900, fontMedium, spacing28, spacing20, spacing40, shade600, lineHeight2, shade100, arc8, spacing16, spacing24, shade000, spacing10, shade900lighten50, spacing12, slow, shade700 } from '@pleo-io/telescope-tokens';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

function _templateObject10() {
  var data = _taggedTemplateLiteralLoose(["\n    position: fixed;\n    top: 0;\n    right: 0;\n    bottom: 0;\n    left: 0;\n    display: flex;\n    flex-direction: column;\n    padding: ", ";\n    overflow: auto;\n    background: ", ";\n    backface-visibility: hidden;\n    scroll-behavior: smooth;\n"]);

  _templateObject10 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9() {
  var data = _taggedTemplateLiteralLoose(["\n    position: relative;\n    width: ", ";\n    margin: auto;\n    padding-top: ", ";\n    background: ", ";\n    border-radius: ", ";\n    outline: none;\n\n    ", ", ", ", ", ", ", ", ", " {\n        margin-bottom: ", ";\n    }\n\n    ", " {\n        margin-bottom: 0;\n    }\n\n    @media (max-width: 768px) {\n        /* TODO agree on breakpoints + mobile first vs desktop first */\n        width: 100%;\n        margin-top: auto;\n        margin-bottom: 0;\n    }\n"]);

  _templateObject9 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8() {
  var data = _taggedTemplateLiteralLoose(["\n    padding: ", " ", ";\n    color: ", ";\n    font-size: ", ";\n    line-height: ", ";\n    background-color: ", ";\n    border-bottom-right-radius: ", ";\n    border-bottom-left-radius: ", ";\n"]);

  _templateObject8 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7() {
  var data = _taggedTemplateLiteralLoose(["\n    display: flex;\n    justify-content: center;\n    margin-bottom: ", ";\n    ", "\n"]);

  _templateObject7 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6() {
  var data = _taggedTemplateLiteralLoose(["\n    display: flex;\n    align-items: center;\n    justify-content: center;\n"]);

  _templateObject6 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5() {
  var data = _taggedTemplateLiteralLoose(["\n    ", "\n    color: ", ";\n"]);

  _templateObject5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4() {
  var data = _taggedTemplateLiteralLoose(["\n    font-size: ", ";\n    ", "\n"]);

  _templateObject4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n    color: ", ";\n    ", "\n"]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n    padding-right: ", ";\n    padding-left: ", ";\n\n    @media (max-width: ", ") {\n        padding-right: ", ";\n        padding-left: ", ";\n    }\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n    ", ";\n    position: absolute;\n    top: 0;\n    right: 0;\n    box-sizing: content-box;\n    width: 14px;\n    height: 16px;\n    margin: 0;\n    padding: ", ";\n    background: none;\n    border: none;\n    border-radius: ", ";\n    cursor: pointer;\n\n    svg {\n        width: 100%;\n        height: 100%;\n        transform: translateY(0);\n        transition-timing-function: ease-in-out;\n        transition-duration: ", ";\n        transition-property: transform;\n        fill: ", ";\n    }\n\n    &:hover svg,\n    &:focus svg {\n        transform: translateY(-2px);\n        fill: ", ";\n    }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
/* TODO agree on breakpoints */


var BreakpointMobile = '778px';
var ModalClose = /*#__PURE__*/styled.button( /*#__PURE__*/_templateObject(), focusRing, spacing12, arc8, slow, shade700, shade900);
var horizontalPadding = /*#__PURE__*/css( /*#__PURE__*/_templateObject2(), spacing40, spacing40, BreakpointMobile, spacing16, spacing16);
/**
 * ModalTitle is a [Title](/components/text/develop/#title) with a few default properties that can't be overridden.
 * Outputs an `h2` element.
 */

var ModalTitle = /*#__PURE__*/styled(Text).attrs({
  variant: '2xlarge-accent',
  align: 'center',
  as: 'h2'
})( /*#__PURE__*/_templateObject3(), shade900, horizontalPadding);
/**
 * ModalSubtitle is a [Title](/components/text/develop/#title) with a few default properties that can't be overridden.
 * Outputs an `h3` element.
 */

var ModalSubtitle = /*#__PURE__*/styled(Text).attrs(function () {
  return {
    variant: 'xlarge-accent',
    align: 'center',
    as: 'h3'
  };
})( /*#__PURE__*/_templateObject4(), fontMedium, horizontalPadding);
/**
 * The main content of the modal, accepts some properties of [Paragraph](/components/text/develop/#paragraph), but outputs a `div` by default.
 * Use the `as` prop to output the fitting semantic element.
 */

var ModalContent = /*#__PURE__*/styled(Text).attrs(function (props) {
  return {
    as: props.as || 'div',
    variant: 'medium-default',
    align: props.align || 'center'
  };
})( /*#__PURE__*/_templateObject5(), horizontalPadding, function (_ref) {
  var color = _ref.color;
  return getColor(color || 'shade700');
});
/**
 * Wrap `<img>`, `<svg>` or other illustration elements to get the default margins.
 */

var ModalIllustration = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject6());
/**
 * Include call to action buttons like "cancel" & "submit" in ModalActions, usually combine with `ButtonGroup`.
 */

var ModalActions = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject7(), spacing28, horizontalPadding);
/**
 * The footer of the modal, accepts some properties of [Paragraph](/components/text/develop/#paragraph), but outputs a `div` by default.
 * Use the `as` prop to output the fitting semantic element.
 */

var ModalFooter = /*#__PURE__*/styled(Text).attrs(function (props) {
  return {
    as: props.as || 'div',
    variant: 'medium-default',
    align: props.align || 'center'
  };
})( /*#__PURE__*/_templateObject8(), spacing20, spacing40, shade600, fontMedium, lineHeight2, shade100, arc8, arc8);
var Modal = /*#__PURE__*/styled(DialogContent)( /*#__PURE__*/_templateObject9(), function (_ref2) {
  var size = _ref2.size;
  return size != null ? size : '500px';
}, spacing24, shade000, arc8, ModalTitle, ModalSubtitle, ModalContent, ModalIllustration, ModalActions, spacing24, ModalFooter);
var ModalBackdrop = /*#__PURE__*/styled(DialogOverlay)( /*#__PURE__*/_templateObject10(), spacing10, shade900lighten50);

var Close = function Close(_ref) {
  var onClick = _ref.onClick,
      props = _objectWithoutPropertiesLoose(_ref, ["onClick"]);

  return React.createElement(ModalClose, Object.assign({
    onClick: onClick
  }, props, {
    title: "Close"
  }), React.createElement(VisuallyHidden, null, "Close"), React.createElement("span", {
    "aria-hidden": true
  }, React.createElement("svg", {
    viewBox: "0 0 386.667 386.667",
    xmlns: "http://www.w3.org/2000/svg"
  }, React.createElement("path", {
    d: "m386.667 45.564-45.564-45.564-147.77 147.769-147.769-147.769-45.564 45.564 147.769 147.769-147.769 147.77 45.564 45.564 147.769-147.769 147.769 147.769 45.564-45.564-147.768-147.77z"
  }))));
};

var Modal$1 = function Modal$1(_ref2) {
  var children = _ref2.children,
      allowPinchZoom = _ref2.allowPinchZoom,
      initialFocusRef = _ref2.initialFocusRef,
      isOpen = _ref2.isOpen,
      onDismiss = _ref2.onDismiss,
      dangerouslyBypassFocusLock = _ref2.dangerouslyBypassFocusLock,
      dangerouslyBypassScrollLock = _ref2.dangerouslyBypassScrollLock,
      props = _objectWithoutPropertiesLoose(_ref2, ["children", "allowPinchZoom", "initialFocusRef", "isOpen", "onDismiss", "dangerouslyBypassFocusLock", "dangerouslyBypassScrollLock"]);

  return React.createElement(ModalBackdrop, {
    isOpen: isOpen,
    onDismiss: onDismiss,
    allowPinchZoom: allowPinchZoom,
    initialFocusRef: initialFocusRef,
    dangerouslyBypassFocusLock: dangerouslyBypassFocusLock,
    dangerouslyBypassScrollLock: dangerouslyBypassScrollLock
  }, React.createElement(Modal, Object.assign({
    "aria-label": props['aria-label'],
    "aria-labelledby": props['aria-labelledby']
  }, props), onDismiss && React.createElement(Close, {
    onClick: onDismiss
  }), children));
};

export { Modal$1 as Modal, ModalActions, ModalContent, ModalFooter, ModalIllustration, ModalSubtitle, ModalTitle };